import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const api = axios.create({
  baseURL: BASE,
  withCredentials: true,
});

api.interceptors.response.use(
  res => res,
  async err => {
    if (err.response?.status === 401) {
      try {
        await axios.post(`${BASE}/auth/refresh`, {}, { withCredentials: true });
        return api.request(err.config);
      } catch {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const formatError = (err) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "An error occurred";
  if (typeof detail === "string") return detail;
  if (typeof detail === "object" && detail.message) return detail.message;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join("; ");
  return JSON.stringify(detail);
};

// Auth
export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  logout: () => api.post("/auth/logout"),
  me: () => api.get("/auth/me"),
  listUsers: (params) => api.get("/auth/users", { params }),
  createUser: (data) => api.post("/auth/users", data),
  inviteUser: (data) => api.post("/auth/invite-user", data),
  resendInvitation: (userId) => api.post("/auth/resend-invitation", { user_id: userId }),
  // LCM-01: User lifecycle management
  editUser: (userId, data) => api.patch(`/auth/users/${userId}`, data),
  deactivateUser: (userId, data) => api.patch(`/auth/users/${userId}/deactivate`, data),
  reactivateUser: (userId) => api.patch(`/auth/users/${userId}/activate`),
  getUserAudit: (userId) => api.get(`/auth/users/${userId}/audit`),
};

// Setup Studio
export const setupApi = {
  listDomains: () => api.get("/setup-studio/domains"),
  getDomain: (code) => api.get(`/setup-studio/domains/${code}`),
  getReadiness: () => api.get("/setup-studio/readiness"),
  getBlockers: (code) => api.get(`/setup-studio/domains/${code}/blockers`),
  validate: (code) => api.post(`/setup-studio/domains/${code}/validate`),
  submitApproval: (code) => api.post(`/setup-studio/domains/${code}/submit-approval`),
  approve: (code) => api.post(`/setup-studio/domains/${code}/approve`),
  activate: (code) => api.post(`/setup-studio/domains/${code}/activate`),
  transition: (code, status, reason) => api.post(`/setup-studio/domains/${code}/transition`, { status, reason }),
};

// Organization
export const orgApi = {
  listLegalEntities: () => api.get("/organization/legal-entities"),
  createLegalEntity: (data) => api.post("/organization/legal-entities", data),
  updateLegalEntity: (id, data) => api.put(`/organization/legal-entities/${id}`, data),
  listOperatingUnits: () => api.get("/organization/operating-units"),
  createOperatingUnit: (data) => api.post("/organization/operating-units", data),
  updateOperatingUnit: (id, data) => api.put(`/organization/operating-units/${id}`, data),
  listLocations: () => api.get("/organization/locations"),
  createLocation: (data) => api.post("/organization/locations", data),
  updateLocation: (id, data) => api.put(`/organization/locations/${id}`, data),
};

// Finance
export const financeApi = {
  listCurrencies: () => api.get("/finance/currencies"),
  listTenantCurrencies: () => api.get("/finance/tenant-currencies"),
  addTenantCurrency: (data) => api.post("/finance/tenant-currencies", data),
  setBaseCurrency: (id) => api.put(`/finance/tenant-currencies/${id}/set-base`),
  listCostCentres: () => api.get("/finance/cost-centres"),
  createCostCentre: (data) => api.post("/finance/cost-centres", data),
  listProfitCentres: () => api.get("/finance/profit-centres"),
  createProfitCentre: (data) => api.post("/finance/profit-centres", data),
  listApprovalAuthorities: () => api.get("/finance/approval-authorities"),
  createApprovalAuthority: (data) => api.post("/finance/approval-authorities", data),
  listPaymentTerms: () => api.get("/finance/payment-terms"),
  createPaymentTerm: (data) => api.post("/finance/payment-terms", data),
  // ERP-FIN-01A: Fiscal Period Management
  listFiscalPeriods: (calendarId) => api.get("/finance/fiscal-periods", calendarId ? { params: { calendar_id: calendarId } } : {}),
  listCalendars: () => api.get("/finance/fiscal-periods/calendars"),
  updatePeriodStatus: (calendarId, periodId, data) => api.patch(`/finance/fiscal-periods/${calendarId}/periods/${periodId}/status`, data),
  getPeriodHistory: (calendarId, periodId) => api.get(`/finance/fiscal-periods/${calendarId}/periods/${periodId}/history`),
  generateFiscalYear: (calendarId, data) => api.post(`/finance/fiscal-periods/${calendarId}/generate`, data),
  getFiscalReadiness: () => api.get("/finance/fiscal-periods/readiness"),
  // COA (ERP-FIN-01B)
  listCOA:              ()        => api.get("/finance/coa/list"),
  createCOA:            (data)    => api.post("/finance/coa", data),
  getCOA:               (id)      => api.get(`/finance/coa/${id}`),
  listCOAVersions:      (coaId)   => api.get(`/finance/coa/${coaId}/versions`),
  listSegments:         (vid)     => api.get(`/finance/coa-versions/${vid}/segments`),
  listValues:           (sid, params) => api.get(`/finance/coa-segments/${sid}/values`, { params }),
  updateValue:          (id, d)   => api.patch(`/finance/coa-values/${id}`, d),
  createValue:          (sid, d)  => api.post(`/finance/coa-segments/${sid}/values`, d),
  getVersionReadiness:  (vid)     => api.get(`/finance/coa-versions/${vid}/readiness`),
  getActivationRequests:(vid)     => api.get(`/finance/coa-versions/${vid}/activation-requests`),
  // ERP-FIN-01B: Migration (Gate 2C)
  getMigrationStatus:   ()        => api.get("/finance/coa/v2/migration/status"),
  runMigration:         (dryRun)  => api.post("/finance/coa/v2/migration/run", { dry_run: dryRun }),
  runAccountingRulesMigration: (dryRun) => api.post("/finance/coa/v2/migration/run-accounting-rules", { dry_run: dryRun }),
  // ERP-FIN-01B: Activation Requests (Gate 2D)
  requestActivation:    (vid, data) => api.post(`/finance/coa-versions/${vid}/request-activation`, data),
  // ERP-FIN-01B: Accounting Rules (Gate 2E)
  listV2AccountingRules:()        => api.get("/finance/coa/v2/accounting-rules"),
};

// ERP-FIN-03: GL Journal (GL-JOURNAL-01)
export const glApi = {
  // Journal CRUD
  listJournals:    (params)             => api.get("/gl/journals", { params }),
  createJournal:   (data)              => api.post("/gl/journals", data),
  getJournal:      (jid)               => api.get(`/gl/journals/${jid}`),
  // Line management
  addLine:         (jid, data)         => api.post(`/gl/journals/${jid}/lines`, data),
  updateLine:      (jid, lid, data)    => api.patch(`/gl/journals/${jid}/lines/${lid}`, data),
  deleteLine:      (jid, lid)          => api.delete(`/gl/journals/${jid}/lines/${lid}`),
  // Lifecycle
  submit:          (jid, data)         => api.post(`/gl/journals/${jid}/submit`, data),
  approve:         (jid, data)         => api.post(`/gl/journals/${jid}/approve`, data),
  reject:          (jid, data)         => api.post(`/gl/journals/${jid}/reject`, data),
  post:            (jid, data)         => api.post(`/gl/journals/${jid}/post`, data),
  voidJournal:     (jid, data)         => api.post(`/gl/journals/${jid}/void`, data),
  reverse:         (jid, data)         => api.post(`/gl/journals/${jid}/reverse`, data),
  // Inspection
  getAudit:        (jid)               => api.get(`/gl/journals/${jid}/audit`),
  validateBalance: (jid)               => api.get(`/gl/validate-balance/${jid}`),
  getTrialBalance: (params)            => api.get("/gl/trial-balance", { params }),
};

// Event Model
export const eventModelApi = {
  listEventTypes: () => api.get("/event-model/event-types"),
  createEventType: (data) => api.post("/event-model/event-types", data),
  updateEventType: (id, data) => api.put(`/event-model/event-types/${id}`, data),
  listServiceCategories: () => api.get("/event-model/service-categories"),
  createServiceCategory: (data) => api.post("/event-model/service-categories", data),
  listServiceDefinitions: () => api.get("/event-model/service-definitions"),
  createServiceDefinition: (data) => api.post("/event-model/service-definitions", data),
};

// Agent Governance
export const governanceApi = {
  listSkills: () => api.get("/agent-governance/skills"),
  createSkill: (data) => api.post("/agent-governance/skills", data),
  listPolicies: () => api.get("/agent-governance/policies"),
  createPolicy: (data) => api.post("/agent-governance/policies", data),
  updatePolicy: (id, data) => api.put(`/agent-governance/policies/${id}`, data),
  listEscalationPolicies: () => api.get("/agent-governance/escalation-policies"),
  createEscalationPolicy: (data) => api.post("/agent-governance/escalation-policies", data),
  listChannels: () => api.get("/agent-governance/communication-channels"),
  createChannel: (data) => api.post("/agent-governance/communication-channels", data),
};

// Customers
export const customerApi = {
  listCustomers: () => api.get("/customers"),
  createCustomer: (data) => api.post("/customers", data),
  getCustomer: (id) => api.get(`/customers/${id}`),
  createContact: (customerId, data) => api.post(`/customers/${customerId}/contacts`, data),
};

// Audit
export const auditApi = {
  listAudit: (params) => api.get("/audit", { params }),
};

// Admin
export const adminApi = {
  listTenants: () => api.get("/admin/tenants"),
};

// P-ARC-01: Capability Catalog & Role Packs — read-only inspection
// P-ARC-02: Tenant role pack customisation
export const catalogApi = {
  listCapabilityCatalog: () => api.get("/admin/capability-catalog"),
  listRolePacks:         () => api.get("/admin/role-packs"),
  getRolePack:           (roleId) => api.get(`/admin/role-packs/${roleId}`),
  // P-ARC-02: Effective role pack with governance metadata (for editor)
  getEffectiveRolePack:  (roleId) => api.get(`/admin/role-packs/${roleId}/effective`),
  // P-ARC-02: Toggle a single capability (grant or revoke)
  toggleCapability:      (roleId, payload) =>
    api.patch(`/admin/role-packs/${roleId}/capabilities/toggle`, payload),
  // P-ARC-02: Reset tenant override — revert to platform default
  resetRolePack:         (roleId, expectedVersion) =>
    api.post(`/admin/role-packs/${roleId}/reset`, { expected_version: expectedVersion }),
};

// ── Stage 01: ONE Engage ──────────────────────────────────────────────────

export const engageApi = {
  getDashboard: () => api.get("/engage/dashboard"),
  getLeadPlanningTemplate: (eventType, planningScope, fields = {}) =>
    api.get("/leads/planning-template", {
      params: {
        event_type:    eventType,
        planning_scope: planningScope,
        guest_count:   fields.guest_count   || undefined,
        location:      fields.location      || undefined,
        budget_range:  fields.budget_range  || undefined,
        venue_status:  fields.venue_status  || undefined,
        event_date:    fields.event_date    || undefined,
      },
    }),
  getChannelStatus: () => api.get("/engage/channel-status"),
  listUsers: () => api.get("/engage/users"),
  getLeadPathConfig: () => api.get("/engage/lead-path-config"),
};

export const followApi = {
  follow: (entityType, entityId) => api.post(`/${entityType.toLowerCase()}s/${entityId}/follow`),
  unfollow: (entityType, entityId) => api.delete(`/${entityType.toLowerCase()}s/${entityId}/follow`),
};

export const taskApi = {
  list: (leadId) => api.get(`/leads/${leadId}/tasks`),
  create: (leadId, data) => api.post(`/leads/${leadId}/tasks`, data),
  complete: (taskId, notes = "") => api.patch(`/tasks/${taskId}/complete`, { notes }),
};

// CRM-AI-ACT-01: Recommendation and AI Action Center APIs
export const recommendationApi = {
  getRecommendations: (leadId, useAi = true) =>
    api.get(`/crm/leads/${leadId}/recommendations`, { params: { use_ai: useAi } }),
  batchCreate: (leadId, items) =>
    api.post(`/crm/leads/${leadId}/recommendations/batch-create`, { items }),
  dismiss: (leadId, recId, action = "SNOOZE", snoozeHours = 48, reason = "") =>
    api.post(`/crm/leads/${leadId}/recommendations/dismiss`, {
      rec_id: recId, action, snooze_hours: snoozeHours, reason: reason || undefined,
    }),
  updateIntentLifecycle: (leadId, data) =>
    api.patch(`/crm/leads/${leadId}/intent-lifecycle`, data),
  validateIntent: (leadId, data) =>
    api.post(`/crm/leads/${leadId}/validate-intent`, data),
  getIntentValidationHistory: (leadId) =>
    api.get(`/crm/leads/${leadId}/intent-validation-history`),
  // 01B: AI draft endpoints
  draftMessage: (leadId, channel, tone, contextNotes = "") =>
    api.post(`/crm/leads/${leadId}/ai-draft`, { channel, tone, context_notes: contextNotes }),
  suggestAgenda: (leadId, meetingPurpose = "Discovery meeting") =>
    api.post(`/crm/leads/${leadId}/ai-agenda`, { meeting_purpose: meetingPurpose }),
  structureNotes: (leadId, rawNotes) =>
    api.post(`/crm/leads/${leadId}/ai-structure-notes`, { raw_notes: rawNotes }),
  // CRM-AI-ACTION-02: mark non-TASK recommendation as actioned after modal confirms
  markActioned: (leadId, recId, actionType, resultingRecordId = null) =>
    api.post(`/crm/leads/${leadId}/recommendations/mark-actioned`, {
      rec_id: recId,
      action_type: actionType,
      resulting_record_id: resultingRecordId,
    }),
};

// CRM-MDM-01: CRM Configuration (Urgency Thresholds + Co-Pilot Help)
export const crmMdmApi = {
  // Urgency Thresholds
  getUrgencyThresholds: () =>
    api.get("/crm/mdm/urgency-thresholds"),
  updateUrgencyThresholds: (criticalDays, highDays) =>
    api.put("/crm/mdm/urgency-thresholds", { critical_days: criticalDays, high_days: highDays }),
  resetUrgencyThresholds: () =>
    api.post("/crm/mdm/urgency-thresholds/reset"),
  // Co-Pilot Help Text
  getCopilotHelp: () =>
    api.get("/crm/mdm/copilot-help"),
  updateSectionTips: (section, tips) =>
    api.put(`/crm/mdm/copilot-help/${section}`, { tips }),
  updatePageTips: (section, page, tips) =>
    api.put(`/crm/mdm/copilot-help/${section}/pages/${encodeURIComponent(page)}`, { tips }),
  resetSectionTips: (section) =>
    api.delete(`/crm/mdm/copilot-help/${section}`),
  resetPageTips: (section, page) =>
    api.delete(`/crm/mdm/copilot-help/${section}/pages/${encodeURIComponent(page)}`),
  // Audit
  getAudit: (limit = 50) =>
    api.get("/crm/mdm/audit", { params: { limit } }),
};

export const relatedApi = {
  getLeadRelated: (leadId) => api.get(`/leads/${leadId}/related`),
};

export const inquiryApi = {
  list: (params) => api.get("/inquiries", { params }),
  get: (id) => api.get(`/inquiries/${id}`),
  createManual: (data) => api.post("/inquiries/manual", data),
  submitWeb: (tenantId, data) => api.post(`/inquiries/web?tenant_id=${tenantId}`, data),
  process: (id) => api.post(`/inquiries/${id}/process`),
};

export const leadApi = {
  list:          (params)         => api.get("/leads", { params }),
  get:           (id)             => api.get(`/leads/${id}`),
  create:        (data)           => api.post("/leads", data),
  update:        (id, data)       => api.patch(`/leads/${id}`, data),
  // Lead Ownership (P-ARC-01R)
  assignOwner:  (id, data)       => api.post(`/leads/${id}/assign-owner`, data),
  claimLead:    (id)             => api.post(`/leads/${id}/claim`, {}),
  // DEPRECATED: use assignOwner; kept for backward compat
  assignPoc:    (id, data)       => api.post(`/leads/${id}/assign-owner`, data),
  transition:    (id, status)     => api.post(`/leads/${id}/transition`, { status }),
  qualify:       (id)             => api.post(`/leads/${id}/qualify`),
  escalate:      (id, data)       => api.post(`/leads/${id}/escalate`, data),
  startPlanning: (id, resolution) => api.post(`/leads/${id}/start-planning`, resolution ? { resolution } : {}),
};

export const conversationApi = {
  getTimeline: (leadId, params) => api.get(`/conversations/${leadId}/timeline`, { params }),
  getConversation: (leadId) => api.get(`/conversations/${leadId}`),
  addMessage: (leadId, data) => api.post(`/conversations/${leadId}/messages`, data),
};

export const followUpApi = {
  list: (params) => api.get("/follow-ups", { params }),
  create: (data) => api.post("/follow-ups", data),
  complete: (id, data) => api.post(`/follow-ups/${id}/complete`, data),
  reschedule: (id, data) => api.post(`/follow-ups/${id}/reschedule`, data),
};

export const escalationApi = {
  list: (params) => api.get("/escalations", { params }),
  resolve: (id, data) => api.post(`/escalations/${id}/resolve`, data),
};

export const agentApi = {
  processInquiry: (inquiryId) => api.post(`/agent/process/${inquiryId}`),
  getPolicyStatus: (leadId) => api.get(`/agent/policy-status/${leadId}`),
  draftReply: (data) => api.post("/agent/draft-reply", data),
  executeReply: (data) => api.post("/agent/execute-reply", data),
  qualify: (leadId) => api.post(`/agent/qualify/${leadId}`),
};

export const knowledgeApi = {
  list: (params) => api.get("/knowledge", { params }),
  create: (data) => api.post("/knowledge", data),
  approve: (id) => api.post(`/knowledge/${id}/approve`),
};

// Stage 01.1 — Email Channel
export const emailChannelApi = {
  getHealth: () => api.get("/email/health"),
  getChannelConfig: () => api.get("/email/channel-config"),
  updateChannelConfig: (data) => api.put("/email/channel-config", data),
  activateChannel: () => api.post("/email/channel-activate"),
  recordTestResult: (data) => api.post("/email/channel-test-record", data),
  testCorrelation: (leadId) => api.post("/email/correlation-test", { lead_id: leadId }),
  listUnmatched: () => api.get("/email/unmatched"),
};

// UAT Insight Agent
export const uatApi = {
  submitFeedback: (data) => api.post("/uat/feedback", data),
  listFeedback: (params) => api.get("/uat/feedback", { params }),
  getFeedback: (id) => api.get(`/uat/feedback/${id}`),
  updateFeedback: (id, data) => api.patch(`/uat/feedback/${id}`, data),
  getDashboard: () => api.get("/uat/dashboard"),
  getClosureGate: () => api.get("/uat/closure-gate"),
  recordSignoff: (data) => api.post("/uat/closure-gate/signoff", data),
};

// Stage 02 — ONE Imagine
export const imagineApi = {
  listEvents: () => api.get("/imagine/events"),
  createFromLead: (data) => api.post("/imagine/events/from-lead", data),
  getEvent: (id) => api.get(`/imagine/events/${id}`),
  getBrief: (id) => api.get(`/imagine/events/${id}/brief`),
  updateBrief: (id, data) => api.put(`/imagine/events/${id}/brief`, data),
  interpretBrief: (id) => api.post(`/imagine/events/${id}/brief/interpret`),
  getBlueprint: (id) => api.get(`/imagine/events/${id}/blueprint`),
  generateBlueprint: (id) => api.post(`/imagine/events/${id}/blueprint/generate`),
  approveBlueprint: (id, bpId, data) => api.post(`/imagine/events/${id}/blueprint/${bpId}/approve`, data),
  validateBlueprint: (id, bpId, data) => api.post(`/imagine/events/${id}/blueprint/${bpId}/customer-validate`, data),
  activateEvent: (id) => api.post(`/imagine/events/${id}/activate`),
};

// Stage 01.1 — Public Inquiry (no auth required)
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL + "/api",
  withCredentials: false,
});

export const publicInquiryApi = {
  getTenantConfig: (alias) => publicApi.get(`/public/tenant/${alias}`),
  submitInquiry: (alias, data) => publicApi.post(`/public/inquire/${alias}`, data),
};

// CRM API
export const crmApi = {
  listAccounts:      (params) => api.get("/crm/accounts", { params }),
  getAccount:        (id)     => api.get(`/crm/accounts/${id}`),
  createAccount:     (data)   => api.post("/crm/accounts", data),
  updateAccount:     (id, data) => api.patch(`/crm/accounts/${id}`, data),
  listContacts:      (params) => api.get("/crm/contacts", { params }),
  getContact:        (id)     => api.get(`/crm/contacts/${id}`),
  createContact:     (data)   => api.post("/crm/contacts", data),
  updateContact:     (id, data) => api.patch(`/crm/contacts/${id}`, data),
  listOpportunities: (params) => api.get("/crm/opportunities", { params }),
  getOpportunity:    (id)     => api.get(`/crm/opportunities/${id}`),
  createOpportunity: (data)   => api.post("/crm/opportunities", data),
  updateOpportunity: (id, data) => api.patch(`/crm/opportunities/${id}`, data),
  // CORE-04: Start Event Planning from a CLOSED_WON Opportunity
  startEventPlanningFromOpp: (oppId) => api.post(`/crm/opportunities/${oppId}/start-planning`),
  listActivities:    (params) => api.get("/crm/activities", { params }),
  search:            (q, limitPerEntity = 5) =>
    api.get("/crm/search", { params: { q, limit_per_entity: limitPerEntity } }),
};

/**
 * Check if a user object has a given capability.
 * Handles wildcards: crm.opportunity.* satisfies crm.opportunity.reassign
 */
export function hasCapability(user, capability) {
  if (!user || !Array.isArray(user.capabilities)) return false;
  const caps = user.capabilities;
  if (caps.includes(capability)) return true;
  const parts = capability.split(".");
  for (let i = 0; i < parts.length; i++) {
    const wildcard = parts.slice(0, i + 1).join(".") + ".*";
    if (caps.includes(wildcard)) return true;
  }
  return false;
}

// ── GQP-LUX: Luxury Event Concierge public API ───────────────────────────────
export const publicLuxApi = {
  /** GET /api/public/event-quote-planner/luxury-templates/{alias} */
  getTemplates: (alias, eventType) =>
    api.get(`/public/event-quote-planner/luxury-templates/${alias}${eventType ? `?event_type=${encodeURIComponent(eventType)}` : ''}`),

  /** POST /api/public/event-quote-planner/luxury-estimate */
  getLuxuryEstimate: (data) =>
    api.post('/public/event-quote-planner/luxury-estimate', data),
};

// ── Gate 3: CRM Composer — real outbound dispatch via canonical gateway ───────
export const crmComposerApi = {
  /** GET /api/crm/leads/{lead_id}/composer-status */
  getStatus: (leadId) =>
    api.get(`/crm/leads/${leadId}/composer-status`),

  /** POST /api/crm/leads/{lead_id}/send-whatsapp */
  sendWhatsApp: (leadId, data) =>
    api.post(`/crm/leads/${leadId}/send-whatsapp`, data),

  /** POST /api/crm/leads/{lead_id}/send-email */
  sendEmail: (leadId, data) =>
    api.post(`/crm/leads/${leadId}/send-email`, data),
};

// ── F1: Public CRM — Provisional Lead creation from BuildMyEvent journey ──────
export const publicLeadApi = {
  createProvisionalLead: (data) =>
    api.post("/public/leads/provisional", data),
};

// ── GQP-03: Public Event Quote Planner ───────────────────────────────────────
export const publicEstimateApi = {
  /** GET /api/public/event-quote-planner/config/{alias} */
  getConfig: (alias) =>
    api.get(`/public/event-quote-planner/config/${alias}`),

  /** GET /api/public/event-quote-planner/service-map/{alias} */
  getServiceMap: (alias) =>
    api.get(`/public/event-quote-planner/service-map/${alias}`),

  /** POST /api/public/event-quote-planner/estimate */
  calculate: (data) =>
    api.post("/public/event-quote-planner/estimate", data),
};

// ── GQP-04: CRM Estimate Integration ─────────────────────────────────────────
export const crmEstimateApi = {
  /** GET /api/pricing/estimates/{ref}/rm-view — full RM-safe view by ref */
  getByRef: (ref) =>
    api.get(`/pricing/estimates/${ref}/rm-view`),

  /** GET /api/pricing/estimates/by-lead/{leadId} — get linked estimate by lead ID */
  getByLeadId: (leadId) =>
    api.get(`/pricing/estimates/by-lead/${leadId}`),
};

// ── F2: COM-01 — WhatsApp Channel Configuration ──────────────────────────────
export const whatsappChannelApi = {
  getConfig:    ()     => api.get("/whatsapp/channel-config"),
  saveConfig:   (data) => api.post("/whatsapp/channel-config", data),
  validate:     ()     => api.post("/whatsapp/channel-config/validate"),
  activate:     ()     => api.post("/whatsapp/channel-config/activate"),
  deactivate:   ()     => api.post("/whatsapp/channel-config/deactivate"),
};

// ── F4: Agent Assist Level 1 ──────────────────────────────────────────────────
export const agentAssistApi = {
  getContext: (leadId) => api.get(`/leads/${leadId}/assist-context`),
};

// ── UEIM-00: Enterprise + Finance + Taxonomy + Context ───────────────────────
export const enterpriseApi = {
  // Enterprise anchor
  listEnterprises:      ()        => api.get("/enterprise"),
  createEnterprise:     (data)    => api.post("/enterprise", data),
  // Legal Entities
  listLegalEntities:    ()        => api.get("/enterprise/legal-entities/list"),
  createLegalEntity:    (data)    => api.post("/enterprise/legal-entities", data),
  updateLegalEntity:    (id, d)   => api.patch(`/enterprise/legal-entities/${id}`, d),
  getLegalEntity:       (id)      => api.get(`/enterprise/legal-entities/${id}`),
  // Divisions
  listDivisions:        ()        => api.get("/enterprise/divisions/list"),
  createDivision:       (data)    => api.post("/enterprise/divisions", data),
  updateDivision:       (id, d)   => api.patch(`/enterprise/divisions/${id}`, d),
  // Business Units
  listBusinessUnits:    ()        => api.get("/enterprise/business-units/list"),
  createBusinessUnit:   (data)    => api.post("/enterprise/business-units", data),
  updateBusinessUnit:   (id, d)   => api.patch(`/enterprise/business-units/${id}`, d),
  getBusinessUnit:      (id)      => api.get(`/enterprise/business-units/${id}`),
  // Profit Centers
  listProfitCenters:    ()        => api.get("/enterprise/profit-centers/list"),
  createProfitCenter:   (data)    => api.post("/enterprise/profit-centers", data),
  // Departments
  listDepartments:      ()        => api.get("/enterprise/departments/list"),
  createDepartment:     (data)    => api.post("/enterprise/departments", data),
  updateDepartment:     (id, d)   => api.patch(`/enterprise/departments/${id}`, d),
  getDepartment:        (id)      => api.get(`/enterprise/departments/${id}`),
  // Teams
  listTeams:            (deptId)  => api.get("/enterprise/teams/list", { params: { department_id: deptId } }),
  createTeam:           (data)    => api.post("/enterprise/teams", data),
  // Cost Centers
  listCostCenters:      ()        => api.get("/enterprise/cost-centers/list"),
  createCostCenter:     (data)    => api.post("/enterprise/cost-centers", data),
  updateCostCenter:     (id, d)   => api.patch(`/enterprise/cost-centers/${id}`, d),
  // Enterprise Mappings
  listMappings:         (params)  => api.get("/enterprise/mappings/list", { params }),
  createMapping:        (data)    => api.post("/enterprise/mappings", data),
  updateMapping:        (id, d)   => api.patch(`/enterprise/mappings/${id}`, d),
  // Opportunity UEIM integration
  updateOpportunityUEIM:(id, d)   => api.patch(`/enterprise/opportunities/${id}/ueim`, d),
};

export const financeFoundationApi = {
  // Ledgers
  listLedgers:          ()        => api.get("/finance/ledgers/list"),
  createLedger:         (data)    => api.post("/finance/ledgers", data),
  updateLedger:         (id, d)   => api.patch(`/finance/ledgers/${id}`, d),
  activateLedger:       (id)      => api.post(`/finance/ledgers/${id}/activate`),
  // Ledger Assignments
  listLedgerAssignments:(leId)    => api.get("/finance/ledger-assignments/list", { params: { legal_entity_id: leId } }),
  createLedgerAssignment:(data)   => api.post("/finance/ledger-assignments", data),
  // Calendars
  listCalendars:        ()        => api.get("/finance/accounting-calendars/list"),
  createCalendar:       (data)    => api.post("/finance/accounting-calendars", data),
  getCalendar:          (id)      => api.get(`/finance/accounting-calendars/${id}`),
  addPeriod:            (id, d)   => api.post(`/finance/accounting-calendars/${id}/periods`, d),
  // Currencies
  listCurrencies:       ()        => api.get("/finance/currencies/list"),
  createCurrency:       (data)    => api.post("/finance/currencies", data),
  // COA
  listCOA:              ()        => api.get("/finance/coa/list"),
  createCOA:            (data)    => api.post("/finance/coa", data),
  getCOA:               (id)      => api.get(`/finance/coa/${id}`),
  listCOAVersions:      (coaId)   => api.get(`/finance/coa/${coaId}/versions`),
  createCOAVersion:     (coaId, d)=> api.post(`/finance/coa/${coaId}/versions`, d),
  activateCOAVersion:   (vid)     => api.post(`/finance/coa-versions/${vid}/activate`),
  listSegments:         (vid)     => api.get(`/finance/coa-versions/${vid}/segments`),
  createSegment:        (vid, d)  => api.post(`/finance/coa-versions/${vid}/segments`, d),
  listValues:           (sid, params) => api.get(`/finance/coa-segments/${sid}/values`, { params }),
  createValue:          (sid, d)  => api.post(`/finance/coa-segments/${sid}/values`, d),
  updateValue:          (id, d)   => api.patch(`/finance/coa-values/${id}`, d),
  getCOAValue:          (id)      => api.get(`/finance/coa-values/${id}`),
  getCOAValueHistory:   (id)      => api.get(`/finance/coa-values/${id}/history`),
  bulkImportValues:     (sid, items, dryRun) =>
      api.post(`/finance/coa-segments/${sid}/values/bulk?dry_run=${dryRun}`, items),
  searchCOAValues:      (q, vid)  => api.get(`/finance/coa/search`, { params: { q, ...(vid ? { version_id: vid } : {}) } }),
  getVersionReadiness:  (vid)     => api.get(`/finance/coa-versions/${vid}/readiness`),
  getCOAVersion:        (vid)     => api.get(`/finance/coa-versions/${vid}`),
  // ERP-FIN-01B: Migration (Gate 2C)
  getMigrationStatus:   ()        => api.get("/finance/coa/v2/migration/status"),
  runMigration:         (dryRun)  => api.post("/finance/coa/v2/migration/run", { dry_run: dryRun }),
  runAccountingRulesMigration: (dryRun) =>
      api.post("/finance/coa/v2/migration/run-accounting-rules", { dry_run: dryRun }),
  // ERP-FIN-01B: Activation Requests (Gate 2D)
  requestActivation:    (vid, data) => api.post(`/finance/coa-versions/${vid}/request-activation`, data),
  getActivationRequests:(vid)     => api.get(`/finance/coa-versions/${vid}/activation-requests`),
  // ERP-FIN-01B: Accounting Rules (Gate 2E)
  listV2AccountingRules:()        => api.get("/finance/coa/v2/accounting-rules"),
  // Accounting Rules
  listAccountingRules:  ()        => api.get("/finance/accounting-rules/list"),
  createAccountingRule: (data)    => api.post("/finance/accounting-rules", data),
};

export const serviceTaxonomyApi = {
  listCategories:       (params)  => api.get("/service-taxonomy/list", { params }),
  createCategory:       (data)    => api.post("/service-taxonomy", data),
  updateCategory:       (id, d)   => api.patch(`/service-taxonomy/${id}`, d),
  getCategory:          (id)      => api.get(`/service-taxonomy/${id}`),
  activate:             (id)      => api.post(`/service-taxonomy/${id}/activate`),
  deactivate:           (id)      => api.post(`/service-taxonomy/${id}/deactivate`),
};

export const accountingContextApi = {
  resolve:              (data)    => api.post("/accounting-context/resolve", data),
  listSnapshots:        (params)  => api.get("/accounting-context/snapshots/list", { params }),
  getSnapshot:          (id)      => api.get(`/accounting-context/snapshots/${id}`),
};

export const lineageApi = {
  listScopeItems:       (oppId)   => api.get("/lineage/scope-items/list", { params: { opportunity_id: oppId } }),
  createScopeItem:      (data)    => api.post("/lineage/scope-items", data),
  updateScopeItem:      (id, d)   => api.patch(`/lineage/scope-items/${id}`, d),
  listRequirements:     (oppId)   => api.get("/lineage/requirement-items/list", { params: { opportunity_id: oppId } }),
  createRequirement:    (data)    => api.post("/lineage/requirement-items", data),
  updateRequirement:    (id, d)   => api.patch(`/lineage/requirement-items/${id}`, d),
};

// ── UEIM-01: Moodboard API ────────────────────────────────────────────────────
const MB = "/moodboard";
export const moodboardApi = {
  // Moodboard CRUD
  create:             (data)            => api.post(`${MB}/`, data),
  list:               (opportunityId)   => api.get(`${MB}/`, { params: { opportunity_id: opportunityId } }),
  get:                (mid)             => api.get(`${MB}/${mid}`),
  update:             (mid, data)       => api.patch(`${MB}/${mid}`, data),

  // Moodboard status actions
  submitReview:       (mid)             => api.post(`${MB}/${mid}/submit-review`),
  approve:            (mid)             => api.post(`${MB}/${mid}/approve`),
  archive:            (mid)             => api.post(`${MB}/${mid}/archive`),

  // Versioning
  createVersion:      (mid)             => api.post(`${MB}/${mid}/version`),
  listVersions:       (mid)             => api.get(`${MB}/${mid}/versions`),
  getVersion:         (mid, vid)        => api.get(`${MB}/${mid}/versions/${vid}`),

  // Design elements
  addElement:         (mid, data)       => api.post(`${MB}/${mid}/elements`, data),
  updateElement:      (mid, eid, data)  => api.patch(`${MB}/${mid}/elements/${eid}`, data),
  deleteElement:      (mid, eid)        => api.delete(`${MB}/${mid}/elements/${eid}`),
  submitElement:      (mid, eid)        => api.post(`${MB}/${mid}/elements/${eid}/submit`),
  approveElement:     (mid, eid)        => api.post(`${MB}/${mid}/elements/${eid}/approve`),
  rejectElement:      (mid, eid, data)  => api.post(`${MB}/${mid}/elements/${eid}/reject`, data || {}),

  // Image upload (multipart) — returns storage_path
  uploadImage:        (mid, eid, formData, type = "primary", caption = "", altText = "") =>
    api.post(
      `${MB}/${mid}/elements/${eid}/upload-image?image_type=${type}&caption=${encodeURIComponent(caption)}&alt_text=${encodeURIComponent(altText)}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    ),

  // Image URL helper (uses cookie auth automatically)
  imageUrl:           (storagePath)     => `${process.env.REACT_APP_BACKEND_URL}/api/moodboard/images/${storagePath}`,

  // Scope conversion
  convertElementToScope: (mid, eid)     => api.post(`${MB}/${mid}/elements/${eid}/to-scope`),
  convertAllToScope:     (mid)          => api.post(`${MB}/${mid}/convert-scope`),

  // Scope items
  listScopeItems:     (params)          => api.get(`${MB}/scope-items`, { params }),
  updateScopeItem:    (sid, data)       => api.patch(`${MB}/scope-items/${sid}`, data),

  // Requirement items
  createRequirement:  (sid, data)       => api.post(`${MB}/scope-items/${sid}/requirements`, data),
  listRequirements:   (sid)             => api.get(`${MB}/scope-items/${sid}/requirements`),
  updateRequirement:  (rid, data)       => api.patch(`${MB}/requirement-items/${rid}`, data),

  // Service categories (from UEIM-00)
  listServiceCategories: ()             => api.get("/service-taxonomy/list"),
};

// ── UEIM-02: Structured Sourcing ─────────────────────────────────────────────
const S = "/ueim02/sourcing";
const SUP = "/ueim02/suppliers";
export const sourcingApi = {
  // Supplier Registry
  listSuppliers:    (p = {}) => api.get(`${SUP}/`, { params: p }),
  getSupplier:      (sid)    => api.get(`${SUP}/${sid}`),
  createSupplier:   (data)   => api.post(`${SUP}/`, data),
  updateSupplier:   (sid, d) => api.patch(`${SUP}/${sid}`, d),
  approveSupplier:  (sid)    => api.post(`${SUP}/${sid}/approve`),
  suspendSupplier:  (sid)    => api.post(`${SUP}/${sid}/suspend`),
  archiveSupplier:  (sid)    => api.post(`${SUP}/${sid}/archive`),

  // Sourcing Packages
  listPackages:     (p = {}) => api.get(`${S}/packages`, { params: p }),
  createPackage:    (data)   => api.post(`${S}/packages`, data),
  getPackage:       (pid)    => api.get(`${S}/packages/${pid}`),
  updatePackage:    (pid, d) => api.patch(`${S}/packages/${pid}`, d),
  openPackage:      (pid)    => api.post(`${S}/packages/${pid}/open`),
  closePackage:     (pid)    => api.post(`${S}/packages/${pid}/close`),

  // Invitations
  createInvitations: (pid, d)           => api.post(`${S}/packages/${pid}/invitations`, d),
  listInvitations:   (pid)              => api.get(`${S}/packages/${pid}/invitations`),
  resendInvitation:  (pid, iid, d)      => api.post(`${S}/packages/${pid}/invitations/${iid}/resend`, d),
  revokeInvitation:  (pid, iid, d)      => api.post(`${S}/packages/${pid}/invitations/${iid}/revoke`, d),
  requestRevision:   (pid, d)           => api.post(`${S}/packages/${pid}/request-revision`, d),

  // Comparison & Selection
  comparePackage:    (pid)    => api.get(`${S}/packages/${pid}/compare`),
  selectLine:        (pid, d) => api.post(`${S}/packages/${pid}/select`, d),
  selectBulk:        (pid, d) => api.post(`${S}/packages/${pid}/select-bulk`, d),
  listSelections:    (pid)    => api.get(`${S}/packages/${pid}/selections`),

  // Public supplier portal (no auth token — uses anonymous axios instance)
  getQuoteView:     (token)   => api.get(`/sourcing/quote/${token}`),
  submitQuote:      (token, d) => api.post(`/sourcing/quote/${token}/submit`, d),
};

// ── PARTNER-NET-01: CRM Partner Directory ─────────────────────────────────────
const PN = "/partner-net";
export const partnerNetApi = {
  // Partner CRUD
  listPartners:       (p = {}) => api.get(`${PN}/partners`, { params: p }),
  getPartner:         (pid)    => api.get(`${PN}/partners/${pid}`),
  createPartner:      (data)   => api.post(`${PN}/partners`, data),
  updatePartner:      (pid, d) => api.patch(`${PN}/partners/${pid}`, d),
  approvePartner:     (pid)    => api.post(`${PN}/partners/${pid}/approve`),
  suspendPartner:     (pid)    => api.post(`${PN}/partners/${pid}/suspend`),
  archivePartner:     (pid)    => api.post(`${PN}/partners/${pid}/archive`),
  // CRM Account Linkage
  linkCRMAccount:     (pid, d) => api.post(`${PN}/partners/${pid}/crm-account`, d),
  unlinkCRMAccount:   (pid)    => api.delete(`${PN}/partners/${pid}/crm-account`),
  // Category Map (Gate PN01-B)
  getCategoryMap:     ()       => api.get(`${PN}/category-map`),
  // Assignment Dry-Run (Gate PN01-E)
  runAssignmentDryRun: ()      => api.post(`${PN}/assignments/dry-run`),
  getLatestDryRun:     ()      => api.get(`${PN}/assignments/dry-run/latest`),
};

// ── UEIM-03: Commercial Costing + Client Proposal ────────────────────────────
const CS = "/ueim03/costing";
const PR = "/ueim03/proposals";
export const commercialApi = {
  // Internal Cost Sheets
  listSheets:       (oppId)    => api.get(`${CS}/sheets`, { params: { opportunity_id: oppId } }),
  createSheet:      (data)     => api.post(`${CS}/sheets`, data),
  getSheet:         (csid)     => api.get(`${CS}/sheets/${csid}`),
  addCostLine:      (csid, d)  => api.post(`${CS}/sheets/${csid}/lines`, d),
  updateCostLine:   (csid, lid, d) => api.put(`${CS}/sheets/${csid}/lines/${lid}`, d),
  removeCostLine:   (csid, lid)    => api.delete(`${CS}/sheets/${csid}/lines/${lid}`),
  submitSheetReview:(csid)     => api.post(`${CS}/sheets/${csid}/submit-review`),
  approveSheet:     (csid, d)  => api.post(`${CS}/sheets/${csid}/approve`, d),
  supersede:        (csid, d)  => api.post(`${CS}/sheets/${csid}/supersede`, d),

  // Client Proposals
  listProposals:    (oppId)    => api.get(`${PR}`, { params: { opportunity_id: oppId } }),
  createProposal:   (data)     => api.post(`${PR}`, data),
  listRevisions:    (pid)      => api.get(`${PR}/${pid}/revisions`),
  createRevision:   (pid, d)   => api.post(`${PR}/${pid}/revisions`, d),
  getRevision:      (pid, rid) => api.get(`${PR}/${pid}/revisions/${rid}`),
  approveRevision:  (pid, rid, d) => api.post(`${PR}/${pid}/revisions/${rid}/approve`, d),
  sendRevision:     (pid, rid, d) => api.post(`${PR}/${pid}/revisions/${rid}/send`, d),
  markViewed:       (pid, rid) => api.post(`${PR}/${pid}/revisions/${rid}/mark-viewed`),
  respond:          (pid, rid, d) => api.post(`${PR}/${pid}/revisions/${rid}/respond`, d),
  pdfUrl:           (pid, rid) => `${api.defaults.baseURL}${PR}/${pid}/revisions/${rid}/pdf`,
};

// ── UEIM-04: Event Core ───────────────────────────────────────────────────────
const EV = "/ueim04/events";
export const eventApi = {
  // Event Masters
  listEvents:         (oppId)    => api.get(`${EV}`, { params: { opportunity_id: oppId } }),
  createEvent:        (data)     => api.post(`${EV}`, data),
  getEvent:           (eid)      => api.get(`${EV}/${eid}`),
  updateEvent:        (eid, d)   => api.patch(`${EV}/${eid}`, d),
  transition:         (eid, targetStatus, notes) => api.post(`${EV}/${eid}/transition`, { notes }, { params: { target_status: targetStatus } }),
  // Scope
  getScope:           (eid)      => api.get(`${EV}/${eid}/scope`),
  // Functions
  listFunctions:      (eid)      => api.get(`${EV}/${eid}/functions`),
  createFunction:     (eid, d)   => api.post(`${EV}/${eid}/functions`, d),
  updateFunction:     (eid, fid, d) => api.patch(`${EV}/${eid}/functions/${fid}`, d),
  // Execution Streams
  listStreams:         (eid)      => api.get(`${EV}/${eid}/streams`),
  createStream:       (eid, d)   => api.post(`${EV}/${eid}/streams`, d),
  updateStream:       (eid, sid, d) => api.patch(`${EV}/${eid}/streams/${sid}`, d),
  addMember:          (eid, sid, d) => api.post(`${EV}/${eid}/streams/${sid}/members`, d),
  listMembers:        (eid, sid) => api.get(`${EV}/${eid}/streams/${sid}/members`),
  removeMember:       (eid, sid, uid) => api.delete(`${EV}/${eid}/streams/${sid}/members/${uid}`),
  // Milestones + Tasks
  listMilestones:     (eid)      => api.get(`${EV}/${eid}/milestones`),
  createMilestone:    (eid, d)   => api.post(`${EV}/${eid}/milestones`, d),
  updateMilestone:    (eid, mid, d) => api.patch(`${EV}/${eid}/milestones/${mid}`, d),
  createTask:         (eid, d)   => api.post(`${EV}/${eid}/tasks`, d),
  updateTask:         (eid, tid, d) => api.patch(`${EV}/${eid}/tasks/${tid}`, d),
  // Partner Assignments
  listPartners:       (eid)      => api.get(`${EV}/${eid}/partner-assignments`),
  createPartner:      (eid, d)   => api.post(`${EV}/${eid}/partner-assignments`, d),
  updatePartner:      (eid, aid, d) => api.patch(`${EV}/${eid}/partner-assignments/${aid}`, d),
  // Readiness + Issues
  listReadiness:      (eid)      => api.get(`${EV}/${eid}/readiness`),
  createReadiness:    (eid, d)   => api.post(`${EV}/${eid}/readiness`, d),
  updateReadiness:    (eid, rid, d) => api.patch(`${EV}/${eid}/readiness/${rid}`, d),
  listIssues:         (eid)      => api.get(`${EV}/${eid}/issues`),
  createIssue:        (eid, d)   => api.post(`${EV}/${eid}/issues`, d),
  updateIssue:        (eid, iid, d) => api.patch(`${EV}/${eid}/issues/${iid}`, d),
  // EventThreads (canonical operating layer)
  getEventThreads:    (eid)      => api.get(`/events/${eid}/threads`),
};

export default api;


// ── UEIM-06: Event Closeout ───────────────────────────────────────────────────
const CO = "/ueim06/closeout/events";
export const closeoutApi = {
  // Sessions
  createSession:         (eid, d)         => api.post(`${CO}/${eid}/sessions`, d),
  listSessions:          (eid)            => api.get(`${CO}/${eid}/sessions`),
  getActiveSession:      (eid)            => api.get(`${CO}/${eid}/sessions/active`),
  transitionSession:     (eid, sid, d)    => api.post(`${CO}/${eid}/sessions/${sid}/transition`, d),
  // Overview
  getOverview:           (eid)            => api.get(`${CO}/${eid}/overview`),
  // Delivery Verifications
  listDelivery:          (eid)            => api.get(`${CO}/${eid}/delivery`),
  updateDelivery:        (eid, vid, d)    => api.patch(`${CO}/${eid}/delivery/${vid}`, d),
  // Supplier Reviews
  listSupplierReviews:   (eid)            => api.get(`${CO}/${eid}/supplier-reviews`),
  createSupplierReview:  (eid, d)         => api.post(`${CO}/${eid}/supplier-reviews`, d),
  updateSupplierReview:  (eid, rid, d)    => api.patch(`${CO}/${eid}/supplier-reviews/${rid}`, d),
  // Stream Reviews
  listStreamReviews:     (eid)            => api.get(`${CO}/${eid}/stream-reviews`),
  createStreamReview:    (eid, d)         => api.post(`${CO}/${eid}/stream-reviews`, d),
  updateStreamReview:    (eid, rid, d)    => api.patch(`${CO}/${eid}/stream-reviews/${rid}`, d),
  // Client Feedback
  getFeedback:           (eid)            => api.get(`${CO}/${eid}/feedback`),
  createFeedback:        (eid, d)         => api.post(`${CO}/${eid}/feedback`, d),
  updateFeedback:        (eid, fid, d)    => api.patch(`${CO}/${eid}/feedback/${fid}`, d),
  // Lessons Learned
  listLessons:           (eid)            => api.get(`${CO}/${eid}/lessons`),
  createLesson:          (eid, d)         => api.post(`${CO}/${eid}/lessons`, d),
  updateLesson:          (eid, lid, d)    => api.patch(`${CO}/${eid}/lessons/${lid}`, d),
  deleteLesson:          (eid, lid)       => api.delete(`${CO}/${eid}/lessons/${lid}`),
  // Gate
  evaluateGate:          (eid)            => api.post(`${CO}/${eid}/gate/evaluate`, {}),
  getLatestGate:         (eid)            => api.get(`${CO}/${eid}/gate/latest`),
  // Close
  closeEvent:            (eid, d)         => api.post(`${CO}/${eid}/close`, d),
  // Audit
  getAudit:              (eid)            => api.get(`${CO}/${eid}/audit`),
};
const OPS = "/ueim05/ops/events";
export const opsApi = {
  // Control Tower
  getControlTower:      (eid)            => api.get(`${OPS}/${eid}/control-tower`),
  // Sessions
  listSessions:         (eid)            => api.get(`${OPS}/${eid}/sessions`),
  getActiveSession:     (eid)            => api.get(`${OPS}/${eid}/sessions/active`),
  createSession:        (eid, d)         => api.post(`${OPS}/${eid}/sessions`, d),
  transitionSession:    (eid, sid, d)    => api.post(`${OPS}/${eid}/sessions/${sid}/transition`, d),
  // Run of Show
  listRunOfShow:        (eid)            => api.get(`${OPS}/${eid}/runofshow`),
  createRosItem:        (eid, d)         => api.post(`${OPS}/${eid}/runofshow`, d),
  updateRosItem:        (eid, rid, d)    => api.patch(`${OPS}/${eid}/runofshow/${rid}`, d),
  transitionRosItem:    (eid, rid, d)    => api.post(`${OPS}/${eid}/runofshow/${rid}/transition`, d),
  // Checkpoints
  listCheckpoints:      (eid)            => api.get(`${OPS}/${eid}/checkpoints`),
  createCheckpoint:     (eid, d)         => api.post(`${OPS}/${eid}/checkpoints`, d),
  transitionCheckpoint: (eid, cid, d)    => api.post(`${OPS}/${eid}/checkpoints/${cid}/transition`, d),
  // Stream Execution Status
  listStreamStatus:     (eid)            => api.get(`${OPS}/${eid}/stream-status`),
  createStreamStatus:   (eid, d)         => api.post(`${OPS}/${eid}/stream-status`, d),
  updateStreamStatus:   (eid, ssid, d)   => api.patch(`${OPS}/${eid}/stream-status/${ssid}`, d),
  // Partner Arrivals
  listPartnerArrivals:  (eid)            => api.get(`${OPS}/${eid}/partner-arrivals`),
  createPartnerArrival: (eid, d)         => api.post(`${OPS}/${eid}/partner-arrivals`, d),
  updatePartnerArrival: (eid, paid, d)   => api.patch(`${OPS}/${eid}/partner-arrivals/${paid}`, d),
  // Team Check-ins
  listTeamCheckins:     (eid)            => api.get(`${OPS}/${eid}/team-checkins`),
  createTeamCheckin:    (eid, d)         => api.post(`${OPS}/${eid}/team-checkins`, d),
  updateTeamCheckin:    (eid, tcid, d)   => api.patch(`${OPS}/${eid}/team-checkins/${tcid}`, d),
  // Live Tasks
  listLiveTasks:        (eid)            => api.get(`${OPS}/${eid}/live-tasks`),
  createLiveTask:       (eid, d)         => api.post(`${OPS}/${eid}/live-tasks`, d),
  updateLiveTask:       (eid, tid, d)    => api.patch(`${OPS}/${eid}/live-tasks/${tid}`, d),
  // Incidents
  listIncidents:        (eid)            => api.get(`${OPS}/${eid}/incidents`),
  createIncident:       (eid, d)         => api.post(`${OPS}/${eid}/incidents`, d),
  updateIncident:       (eid, iid, d)    => api.patch(`${OPS}/${eid}/incidents/${iid}`, d),
  // Go/No-Go
  evaluateGate:         (eid, d)         => api.post(`${OPS}/${eid}/go-no-go/evaluate`, d),
  getLatestGate:        (eid)            => api.get(`${OPS}/${eid}/go-no-go/latest`),
  overrideGate:         (eid, d)         => api.post(`${OPS}/${eid}/go-no-go/override`, d),
  // Go Live
  goLive:               (eid, d)         => api.post(`${OPS}/${eid}/go-live`, d),
  // Audit
  getAudit:             (eid)            => api.get(`${OPS}/${eid}/audit`),
};

// ── DCC-01 Document Management ────────────────────────────────────────────────
const DOC = "/dcc01/events";
export const documentApi = {
  // Config
  getConfig: ()                          => api.get("/dcc01/config"),
  // Document CRUD
  createDocument: (eid, d)               => api.post(`${DOC}/${eid}/documents`, d),
  listDocuments: (eid, params)           => api.get(`${DOC}/${eid}/documents`, { params }),
  getSummary: (eid)                      => api.get(`${DOC}/${eid}/documents/summary`),
  getDocument: (eid, did)                => api.get(`${DOC}/${eid}/documents/${did}`),
  getVersions: (eid, did)               => api.get(`${DOC}/${eid}/documents/${did}/versions`),
  // Upload (multipart/form-data)
  uploadFile: (eid, did, file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`${DOC}/${eid}/documents/${did}/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
    });
  },
  // Download (returns blob)
  downloadFile: (eid, did)              => api.get(`${DOC}/${eid}/documents/${did}/download`, { responseType: "blob" }),
  // Lifecycle
  transitionLifecycle: (eid, did, d)    => api.patch(`${DOC}/${eid}/documents/${did}/lifecycle`, d),
  // Visibility
  updateVisibility: (eid, did, d)       => api.patch(`${DOC}/${eid}/documents/${did}/visibility`, d),
  // Archive / soft-delete
  archiveDocument: (eid, did)           => api.delete(`${DOC}/${eid}/documents/${did}`),
  // Thread card integration
  linkDocumentToCard: (eid, tid, cid, d) => api.patch(`${DOC}/${eid}/threads/${tid}/cards/${cid}/link-document`, d),
  getCardDocument: (eid, tid, cid)      => api.get(`${DOC}/${eid}/threads/${tid}/cards/${cid}/document`),
};

// ── DCC-02 Canonical Communications ──────────────────────────────────────────
const COMMS = "/dcc02/events";
export const commsApi = {
  // Config
  getConfig: ()                              => api.get("/dcc02/config"),
  // Communication lifecycle
  compose: (eid, d)                          => api.post(`${COMMS}/${eid}/communications`, d),
  send: (eid, cid, d = {})                   => api.post(`${COMMS}/${eid}/communications/${cid}/send`, d),
  list: (eid, params)                        => api.get(`${COMMS}/${eid}/communications`, { params }),
  getSummary: (eid)                          => api.get(`${COMMS}/${eid}/communications/summary`),
  get: (eid, cid)                            => api.get(`${COMMS}/${eid}/communications/${cid}`),
  // MESSAGE card dispatch
  dispatchCard: (eid, tid, cid, d)           => api.post(`${COMMS}/${eid}/threads/${tid}/cards/${cid}/dispatch`, d),
  // Templates
  listTemplates: (eid, params)               => api.get(`${COMMS}/${eid}/templates`, { params }),
  createTemplate: (eid, d)                   => api.post(`${COMMS}/${eid}/templates`, d),
};

// ── DCC-03 Notifications ──────────────────────────────────────────────────────
export const notificationsApi = {
  // Enum config
  getConfig:      ()             => api.get("/dcc03/config"),
  // Feed — user's own notifications
  list:           (params)       => api.get("/dcc03/notifications", { params }),
  getUnreadCount: ()             => api.get("/dcc03/notifications/unread-count"),
  markRead:       (nid)          => api.patch(`/dcc03/notifications/${nid}/read`),
  markAllRead:    ()             => api.post("/dcc03/notifications/read-all"),
  dismiss:        (nid)          => api.delete(`/dcc03/notifications/${nid}`),
  // Admin view (notification.manage required)
  adminList:      (params)       => api.get("/dcc03/admin/notifications", { params }),
};

// ── A08: Customer Bookings ────────────────────────────────────────────────────
export const bookingApi = {
  list:         (params)        => api.get("/a08/bookings", { params }),
  get:          (bid)           => api.get(`/a08/bookings/${bid}`),
  create:       (d)             => api.post("/a08/bookings", d),
  update:       (bid, d)        => api.patch(`/a08/bookings/${bid}`, d),
  submit:       (bid, d = {})   => api.post(`/a08/bookings/${bid}/submit`, d),
  confirm:      (bid, d)        => api.post(`/a08/bookings/${bid}/confirm`, d),
  complete:     (bid, d = {})   => api.post(`/a08/bookings/${bid}/complete`, d),
  cancel:       (bid, d)        => api.post(`/a08/bookings/${bid}/cancel`, d),
  addMilestone: (bid, d)        => api.post(`/a08/bookings/${bid}/milestones`, d),
  updateMilestone: (bid, mid, d) => api.patch(`/a08/bookings/${bid}/milestones/${mid}`, d),
  addAmendment: (bid, d)        => api.post(`/a08/bookings/${bid}/amendments`, d),
};

// ── A05: Event Requirements ───────────────────────────────────────────────────
export const requirementsApi = {
  list:             (params)      => api.get("/a05/requirements", { params }),
  get:              (rid)         => api.get(`/a05/requirements/${rid}`),
  create:           (d)           => api.post("/a05/requirements", d),
  update:           (rid, d)      => api.patch(`/a05/requirements/${rid}`, d),
  transition:       (rid, d)      => api.post(`/a05/requirements/${rid}/transition`, d),
  addChangeRequest: (rid, d)      => api.post(`/a05/requirements/${rid}/change-requests`, d),
  decideChangeRequest: (rid, crid, d) => api.patch(`/a05/requirements/${rid}/change-requests/${crid}`, d),
};

// ── A06: Service Catalog ──────────────────────────────────────────────────────
export const serviceCatalogApi = {
  getConfig:          ()            => api.get("/a06/catalog/config"),
  // Services
  listServices:       (params)      => api.get("/a06/catalog/services", { params }),
  getService:         (sid)         => api.get(`/a06/catalog/services/${sid}`),
  createService:      (d)           => api.post("/a06/catalog/services", d),
  updateService:      (sid, d)      => api.patch(`/a06/catalog/services/${sid}`, d),
  publishService:     (sid, d = {}) => api.post(`/a06/catalog/services/${sid}/publish`, d),
  archiveService:     (sid, d = {}) => api.post(`/a06/catalog/services/${sid}/archive`, d),
  addPriceVersion:    (sid, d)      => api.post(`/a06/catalog/services/${sid}/price-versions`, d),
  // Packages
  listPackages:       (params)      => api.get("/a06/catalog/packages", { params }),
  getPackage:         (pid)         => api.get(`/a06/catalog/packages/${pid}`),
  createPackage:      (d)           => api.post("/a06/catalog/packages", d),
  updatePackage:      (pid, d)      => api.patch(`/a06/catalog/packages/${pid}`, d),
  publishPackage:     (pid)         => api.post(`/a06/catalog/packages/${pid}/publish`),
  archivePackage:     (pid)         => api.post(`/a06/catalog/packages/${pid}/archive`),
};

// ── WVB-01: Event Graph (Wave B1) ─────────────────────────────────────────────
const GR = "/wvb/events";
export const graphApi = {
  getGraph:   (eid, params) => api.get(`${GR}/${eid}/graph`, { params }),
  getSummary: (eid)         => api.get(`${GR}/${eid}/graph/summary`),
};

// ── WVB-02: Event Budget (Wave B2) ────────────────────────────────────────────
const BR = "/wvb/events";
export const budgetApi = {
  getFinancials: (eid)           => api.get(`${BR}/${eid}/financials`),
  createBudget:  (eid, d)        => api.post(`${BR}/${eid}/budget`, d),
  getBudget:     (eid)           => api.get(`${BR}/${eid}/budget`),
  getBudgetById: (eid, bid)      => api.get(`${BR}/${eid}/budget/${bid}`),
  updateBudget:  (eid, bid, d)   => api.patch(`${BR}/${eid}/budget/${bid}`, d),
  submitBudget:  (eid, bid)      => api.post(`${BR}/${eid}/budget/${bid}/submit`),
  reviseBudget:  (eid, bid)      => api.post(`${BR}/${eid}/budget/${bid}/revise`),
  newVersion:    (eid, bid, notes) => api.post(`${BR}/${eid}/budget/${bid}/version`, { notes }),
  getLines:      (eid, bid)      => api.get(`${BR}/${eid}/budget/${bid}/lines`),
  addLine:       (eid, bid, d)   => api.post(`${BR}/${eid}/budget/${bid}/lines`, d),
  updateLine:    (eid, bid, lid, d) => api.patch(`${BR}/${eid}/budget/${bid}/lines/${lid}`, d),
  deleteLine:    (eid, bid, lid) => api.delete(`${BR}/${eid}/budget/${bid}/lines/${lid}`),
  getVersions:   (eid)           => api.get(`${BR}/${eid}/versions`),
};

// ── WVB-03: Approval Governance API ────────────────────────────────────────
const AP = `/wvb`;

export const approvalApi = {
  // Policy management (budget.policy.manage)
  listPolicies:       ()        => api.get(`${AP}/approval-policies`),
  createPolicy:       (d)       => api.post(`${AP}/approval-policies`, d),
  getPolicy:          (pid)     => api.get(`${AP}/approval-policies/${pid}`),
  updatePolicy:       (pid, d)  => api.patch(`${AP}/approval-policies/${pid}`, d),
  deactivatePolicy:   (pid)     => api.post(`${AP}/approval-policies/${pid}/deactivate`),

  // Approval workflow (budget.approve / budget.reject / budget.request_changes)
  getApprovalRequest: (eid, bid)          => api.get(`${AP}/events/${eid}/budget/${bid}/approval`),
  getApprovalHistory: (eid, bid)          => api.get(`${AP}/events/${eid}/budget/${bid}/approval/history`),
  approve:            (eid, bid, rid, d)  => api.post(`${AP}/events/${eid}/budget/${bid}/approval/${rid}/approve`, d),
  reject:             (eid, bid, rid, d)  => api.post(`${AP}/events/${eid}/budget/${bid}/approval/${rid}/reject`, d),
  requestChanges:     (eid, bid, rid, d)  => api.post(`${AP}/events/${eid}/budget/${bid}/approval/${rid}/request-changes`, d),
  withdraw:           (eid, bid, rid)     => api.post(`${AP}/events/${eid}/budget/${bid}/approval/${rid}/withdraw`),

  // Inbox (budget.approve)
  inbox:              ()         => api.get(`${AP}/approval-inbox`),

  // Supersession (budget.supersede)
  supersede:          (eid, bid, notes) => api.post(`${AP}/events/${eid}/budget/${bid}/supersede`, { notes }),
};

// ── WVB-04: Financial Integration API ─────────────────────────────────────────
export const financialApi = {
  getContext:       (eid)     => api.get(`/wvb/events/${eid}/financial-context`),
  getDashboard:     (params)  => api.get(`/wvb/financial-dashboard`, { params }),
  getPolicy:        ()        => api.get(`/wvb/financial-policy`),
  setPolicy:        (d)       => api.post(`/wvb/financial-policy`, d),
};

// ── GQP-01: Pricing Studio API ────────────────────────────────────────────────
const PRICING_PFX = "/pricing";
export const pricingApi = {
  // Config / LOV
  getConfig:              ()          => api.get(`${PRICING_PFX}/config`),

  // Rate Cards
  listRateCards:          (params)    => api.get(`${PRICING_PFX}/rate-cards`, { params }),
  createRateCard:         (d)         => api.post(`${PRICING_PFX}/rate-cards`, d),
  getRateCard:            (rcid)      => api.get(`${PRICING_PFX}/rate-cards/${rcid}`),
  updateRateCard:         (rcid, d)   => api.patch(`${PRICING_PFX}/rate-cards/${rcid}`, d),
  // Rate Card Lines
  addLine:                (rcid, d)   => api.post(`${PRICING_PFX}/rate-cards/${rcid}/lines`, d),
  updateLine:             (rcid, lid, d) => api.patch(`${PRICING_PFX}/rate-cards/${rcid}/lines/${lid}`, d),
  removeLine:             (rcid, lid) => api.delete(`${PRICING_PFX}/rate-cards/${rcid}/lines/${lid}`),
  // Rate Card Lifecycle
  submitForReview:        (rcid)      => api.post(`${PRICING_PFX}/rate-cards/${rcid}/submit-review`),
  publishRateCard:        (rcid)      => api.post(`${PRICING_PFX}/rate-cards/${rcid}/publish`),
  archiveRateCard:        (rcid, d)   => api.post(`${PRICING_PFX}/rate-cards/${rcid}/archive`, d),
  newVersion:             (rcid, d)   => api.post(`${PRICING_PFX}/rate-cards/${rcid}/new-version`, d),
  getRateCardHistory:     (rcid)      => api.get(`${PRICING_PFX}/rate-cards/${rcid}/history`),

  // Pricing Adjustments
  listAdjustments:        (params)    => api.get(`${PRICING_PFX}/adjustments`, { params }),
  createAdjustment:       (d)         => api.post(`${PRICING_PFX}/adjustments`, d),
  getAdjustment:          (aid)       => api.get(`${PRICING_PFX}/adjustments/${aid}`),
  updateAdjustment:       (aid, d)    => api.patch(`${PRICING_PFX}/adjustments/${aid}`, d),
  activateAdjustment:     (aid)       => api.post(`${PRICING_PFX}/adjustments/${aid}/activate`),
  deactivateAdjustment:   (aid)       => api.post(`${PRICING_PFX}/adjustments/${aid}/deactivate`),

  // Tax Mappings
  listTaxMappings:        (params)    => api.get(`${PRICING_PFX}/tax-mappings`, { params }),
  createTaxMapping:       (d)         => api.post(`${PRICING_PFX}/tax-mappings`, d),
  getTaxMapping:          (tid)       => api.get(`${PRICING_PFX}/tax-mappings/${tid}`),
  updateTaxMapping:       (tid, d)    => api.patch(`${PRICING_PFX}/tax-mappings/${tid}`, d),
  deactivateTaxMapping:   (tid)       => api.post(`${PRICING_PFX}/tax-mappings/${tid}/deactivate`),

  // Estimate Configuration
  getEstimateConfig:      ()          => api.get(`${PRICING_PFX}/estimate-config`),
  updateEstimateConfig:   (d)         => api.put(`${PRICING_PFX}/estimate-config`, d),

  // Version History
  getVersionHistory:      (params)    => api.get(`${PRICING_PFX}/version-history`, { params }),

  // Simulator
  getSimulatorStatus:     ()          => api.get(`${PRICING_PFX}/simulate/status`),
};

// ── ERP-FIN-02: AR Invoice Foundation (Gate AR-01) ────────────────────────────
const AR = "/ar";

export const arInvoiceApi = {
  // List & Get
  list:                   (params)        => api.get(`${AR}/invoices`, { params }),
  get:                    (iid)           => api.get(`${AR}/invoices/${iid}`),
  getAudit:               (iid)           => api.get(`${AR}/invoices/${iid}/audit`),
  getCustomerInvoices:    (cid)           => api.get(`${AR}/customers/${cid}/invoices`),
  getMilestoneStatus:     (revId)         => api.get(`${AR}/invoices/milestone-status/${revId}`),

  // Create
  create:                 (data)          => api.post(`${AR}/invoices`, data),
  createFromMilestone:    (data)          => api.post(`${AR}/invoices/from-milestone`, data),

  // Update (DRAFT only)
  update:                 (iid, data)     => api.patch(`${AR}/invoices/${iid}`, data),

  // Line management (DRAFT only)
  addLine:                (iid, data)     => api.post(`${AR}/invoices/${iid}/lines`, data),
  updateLine:             (iid, lid, d)   => api.patch(`${AR}/invoices/${iid}/lines/${lid}`, d),
  deleteLine:             (iid, lid)      => api.delete(`${AR}/invoices/${iid}/lines/${lid}`),

  // Lifecycle transitions
  approve:                (iid, data)     => api.post(`${AR}/invoices/${iid}/approve`, data),
  reject:                 (iid, data)     => api.post(`${AR}/invoices/${iid}/reject`, data),
  issue:                  (iid, data)     => api.post(`${AR}/invoices/${iid}/issue`, data),
  void:                   (iid, data)     => api.post(`${AR}/invoices/${iid}/void`, data),

  // PDF (ISSUED invoices)
  getPdfUrl:              (iid)           => `${process.env.REACT_APP_BACKEND_URL}/api${AR}/invoices/${iid}/pdf`,
};

// ── ERP-FIN-02: AR Receipt Foundation (Gate AR-02) ────────────────────────────
// AR-ATOMIC-01 Protocol: idempotent, atomic-conditional, crash-safe allocation

export const arReceiptApi = {
  // ── AR-02A: Receipt Management ────────────────────────────────────────────
  list:                    (params)                 => api.get(`${AR}/receipts`, { params }),
  get:                     (rid)                    => api.get(`${AR}/receipts/${rid}`),
  create:                  (data)                   => api.post(`${AR}/receipts`, data),
  update:                  (rid, data)              => api.patch(`${AR}/receipts/${rid}`, data),
  verify:                  (rid, data)              => api.post(`${AR}/receipts/${rid}/verify`, data),
  reverse:                 (rid, data)              => api.post(`${AR}/receipts/${rid}/reverse`, data),
  getAudit:                (rid)                    => api.get(`${AR}/receipts/${rid}/audit`),
  getAllocations:           (rid)                    => api.get(`${AR}/receipts/${rid}/allocations`),

  // ── AR-02B: Allocation Engine (AR-ATOMIC-01) ──────────────────────────────
  allocate:                (rid, data)              => api.post(`${AR}/receipts/${rid}/allocate`, data),
  allocateBatch:           (rid, data)              => api.post(`${AR}/receipts/${rid}/allocate-batch`, data),
  reverseAllocation:       (aid, data)              => api.post(`${AR}/receipt-allocations/${aid}/reverse`, data),
  staleOperations:         ()                       => api.get(`${AR}/recovery/stale-operations`),

  // ── AR Advances ───────────────────────────────────────────────────────────
  listAdvances:            (params)                 => api.get(`${AR}/advances`, { params }),
  createAdvance:           (data)                   => api.post(`${AR}/advances`, data),
  getAdvance:              (advId)                  => api.get(`${AR}/advances/${advId}`),
  applyAdvance:            (advId, data)            => api.post(`${AR}/advances/${advId}/apply`, data),

  // ── AR Customer Summary ───────────────────────────────────────────────────
  getCustomerOutstanding:  (cid)                    => api.get(`${AR}/customers/${cid}/outstanding`),
  getCustomerAging:        (cid)                    => api.get(`${AR}/customers/${cid}/aging`),
  getCustomerReceipts:     (cid, params)            => api.get(`${AR}/customers/${cid}/receipts`, { params }),
};

// ── ERP-FIN-02: AR-03 Credit Notes, Refunds ───────────────────────────────────
export const arCreditNoteApi = {
  // Credit Notes
  list:                    (params)                 => api.get(`${AR}/credit-notes`, { params }),
  get:                     (cnId)                   => api.get(`${AR}/credit-notes/${cnId}`),
  create:                  (data)                   => api.post(`${AR}/credit-notes`, data),
  update:                  (cnId, data)             => api.patch(`${AR}/credit-notes/${cnId}`, data),
  approve:                 (cnId, data)             => api.post(`${AR}/credit-notes/${cnId}/approve`, data),
  reject:                  (cnId, data)             => api.post(`${AR}/credit-notes/${cnId}/reject`, data),
  issue:                   (cnId, data)             => api.post(`${AR}/credit-notes/${cnId}/issue`, data),
  void:                    (cnId, data)             => api.post(`${AR}/credit-notes/${cnId}/void`, data),
  getPdfUrl:               (cnId)                   => `${process.env.REACT_APP_BACKEND_URL}/api${AR}/credit-notes/${cnId}/pdf`,
  getAudit:                (cnId)                   => api.get(`${AR}/credit-notes/${cnId}/audit`),
  getApplications:         (cnId)                   => api.get(`${AR}/credit-notes/${cnId}/applications`),

  // Credit Application (CN-ATOMIC-01)
  apply:                   (cnId, data)             => api.post(`${AR}/credit-notes/${cnId}/apply`, data),
  reverseApplication:      (aid, data)              => api.post(`${AR}/credit-applications/${aid}/reverse`, data),

  // Customer credit balance
  getCustomerCreditBalance: (cid)                   => api.get(`${AR}/customers/${cid}/credit-balance`),

  // Invoice lookup with credit eligibility (for credit note creation)
  getInvoiceForCredit:     (invId)                  => api.get(`${AR}/invoices/${invId}/credit-eligibility`),

  // Refunds
  listRefunds:             (params)                 => api.get(`${AR}/refunds`, { params }),
  getRefund:               (rfId)                   => api.get(`${AR}/refunds/${rfId}`),
  createRefund:            (data)                   => api.post(`${AR}/refunds`, data),
  submitRefund:            (rfId, data)             => api.post(`${AR}/refunds/${rfId}/submit`, data),
  approveRefund:           (rfId, data)             => api.post(`${AR}/refunds/${rfId}/approve`, data),
  rejectRefund:            (rfId, data)             => api.post(`${AR}/refunds/${rfId}/reject`, data),
  cancelRefund:            (rfId, data)             => api.post(`${AR}/refunds/${rfId}/cancel`, data),
  settleRefund:            (rfId, data)             => api.post(`${AR}/refunds/${rfId}/settle`, data),
  getRefundAudit:          (rfId)                   => api.get(`${AR}/refunds/${rfId}/audit`),

  // MDM (AR-03 config types via shared MDM router)
  getMDMConfig:            (configType, includeInactive = false) =>
    api.get(`${AR}/mdm/${configType}`, { params: { include_inactive: includeInactive } }),
  createMDMItem:           (configType, data)       => api.post(`${AR}/mdm/${configType}`, data),
  updateMDMItem:           (configType, itemId, data) => api.patch(`${AR}/mdm/${configType}/${itemId}`, data),
  deactivateMDMItem:       (configType, itemId, data) => api.post(`${AR}/mdm/${configType}/${itemId}/deactivate`, data),
  activateMDMItem:         (configType, itemId)     => api.post(`${AR}/mdm/${configType}/${itemId}/activate`),
};

// ── ERP-FIN-02 Gate AR-04/05: Customer Statements, Aging & Accounting Readiness ─
export const arStatementApi = {
  // AR-04A: Statement preview (as-of-date, historical balance calculation)
  getPreview:          (cid, params)  => api.get(`${AR}/customers/${cid}/statement`, { params }),

  // AR-04A: Issue a formal statement (PDF + DCC archival)
  issue:               (cid, data)   => api.post(`${AR}/customers/${cid}/statement/issue`, data),

  // AR-04A: List issued statements for a customer
  list:                (params)      => api.get(`${AR}/statements`, { params }),

  // AR-04A: Get a single issued statement
  get:                 (stmtId)      => api.get(`${AR}/statements/${stmtId}`),

  // AR-04A: Audit trail for a statement
  getAudit:            (stmtId)      => api.get(`${AR}/statements/${stmtId}/audit`),

  // AR-04B: AR Aging aggregate (bucket summary + customer drill-down)
  getAging:            (params)      => api.get(`${AR}/aging`, { params }),

  // AR-04B: Aging for a single customer
  getCustomerAging:    (cid, params) => api.get(`${AR}/customers/${cid}/aging`, { params }),

  // AR-05: Accounting readiness audit (read-only)
  getAccountingReadiness: (params)   => api.get(`${AR}/accounting-readiness`, { params }),
};

// ── AP-EVENT-01 Gate EFIN-01/02/04: Event Finance Attribution & Metrics ───────
export const eventFinanceApi = {
  // EFIN-02: Full financial metrics for an event (15 metrics)
  getMetrics:           (eid, params)         => api.get(`/events/${eid}/finance/metrics`, { params }),

  // EFIN-01: Shared-cost allocations
  listAllocations:      (eid, params)         => api.get(`/events/${eid}/finance/allocations`, { params }),
  createAllocation:     (eid, data)           => api.post(`/events/${eid}/finance/allocations`, data),
  updateAllocation:     (eid, aid, data)      => api.patch(`/events/${eid}/finance/allocations/${aid}`, data),
  submitAllocation:     (eid, aid)            => api.post(`/events/${eid}/finance/allocations/${aid}/submit`),
  approveAllocation:    (eid, aid, data)      => api.post(`/events/${eid}/finance/allocations/${aid}/approve`, data),
  reverseAllocation:    (eid, aid, data)      => api.post(`/events/${eid}/finance/allocations/${aid}/reverse`, data),

  // EFIN-04: Event Finance MDM
  listMDM:              (configType, params)  => api.get(`/events/finance/mdm/${configType}`, { params }),
  getMDMItem:           (configType, id)      => api.get(`/events/finance/mdm/${configType}/${id}`),
  getMDMHistory:        (configType, id)      => api.get(`/events/finance/mdm/${configType}/${id}/history`),
  createMDM:            (configType, data)    => api.post(`/events/finance/mdm/${configType}`, data),
  updateMDM:            (configType, id, data) => api.patch(`/events/finance/mdm/${configType}/${id}`, data),
  deactivateMDM:        (configType, id, data) => api.post(`/events/finance/mdm/${configType}/${id}/deactivate`, data),
  activateMDM:          (configType, id)      => api.post(`/events/finance/mdm/${configType}/${id}/activate`),
};

// ── E2E-04E: Event Operations MDM ────────────────────────────────────────────
export const opsSetupApi = {
  listConfig:        (configType, params)      => api.get(`/ops-mdm/config/${configType}`, { params }),
  getConfig:         (configType, id)          => api.get(`/ops-mdm/config/${configType}/${id}`),
  getConfigHistory:  (configType, id)          => api.get(`/ops-mdm/config/${configType}/${id}/history`),
  createConfig:      (configType, data)        => api.post(`/ops-mdm/config/${configType}`, data),
  updateConfig:      (configType, id, data)    => api.patch(`/ops-mdm/config/${configType}/${id}`, data),
  activateConfig:    (configType, id)          => api.post(`/ops-mdm/config/${configType}/${id}/activate`),
  deactivateConfig:  (configType, id)          => api.post(`/ops-mdm/config/${configType}/${id}/deactivate`),
  previewActivation: (eid)                     => api.get(`/ops-mdm/thread-templates/activation-preview/${eid}`),

  // E2E-04C: Card projections
  projectCustomerComm:   (eid, data)           => api.post(`/events/${eid}/threads/cards/customer-communication`, data),
  projectDeliveryEvidence:(eid, data)          => api.post(`/events/${eid}/threads/cards/delivery-evidence`, data),
  projectFinancialSummary:(eid, data)          => api.post(`/events/${eid}/threads/cards/financial-summary`, data),
  projectException:      (eid, data)           => api.post(`/events/${eid}/threads/cards/operational-exception`, data),
  // E2E-04H: Control Tower event-scoped summaries
  getSourcingSummary:    (eid)                 => api.get(`/events/${eid}/sourcing-summary`),
  getAcceptanceSummary:  (eid)                 => api.get(`/events/${eid}/acceptance-summary`),
};

// ── PAY-01B: Payment Configuration (Bank Accounts + UPI Merchants) ────────────
export const payConfigApi = {
  // Bank accounts and cash desks
  listBankAccounts:          ()              => api.get("/finance/bank-accounts"),
  createBankAccount:         (data)         => api.post("/finance/bank-accounts", data),
  createCashDesk:            (data)         => api.post("/finance/cash-desks", data),
  updateBankAccount:         (id, data)     => api.patch(`/finance/bank-accounts/${id}`, data),
  getBankAccount:            (id)           => api.get(`/finance/bank-accounts/${id}`),

  // UPI merchant configuration
  listUPIMerchants:          ()              => api.get("/finance/upi-merchants"),
  getActiveUPIMerchant:      (channel)      => api.get("/finance/upi-merchants/active", { params: { channel } }),
  createUPIMerchant:         (data)         => api.post("/finance/upi-merchants", data),
  getUPIMerchant:            (id)           => api.get(`/finance/upi-merchants/${id}`),
  updateUPIMerchant:         (id, data)     => api.patch(`/finance/upi-merchants/${id}`, data),
  getUPIQR:                  (id, amount)   => api.get(`/finance/upi-merchants/${id}/qr`, { params: amount ? { amount } : {} }),
};


// ── PAY-01C: AR Payment Plans, Installments and Mock Reminders ────────────────
export const arPayPlanApi = {
  // Plan CRUD
  list:              (params)          => api.get(`${AR}/payment-plans`, { params }),
  create:            (data)            => api.post(`${AR}/payment-plans`, data),
  get:               (planId)          => api.get(`${AR}/payment-plans/${planId}`),
  update:            (planId, data)    => api.patch(`${AR}/payment-plans/${planId}`, data),
  activate:          (planId)          => api.post(`${AR}/payment-plans/${planId}/activate`),
  cancel:            (planId, data)    => api.post(`${AR}/payment-plans/${planId}/cancel`, data),

  // Templates & Summary
  getTemplates:      ()                => api.get(`${AR}/payment-plans/templates`),
  getSummary:        ()                => api.get(`${AR}/payment-plans/summary`),

  // Installments
  listInstallments:  (planId)          => api.get(`${AR}/payment-plans/${planId}/installments`),
  getInstallment:    (planId, instId)  => api.get(`${AR}/payment-plans/${planId}/installments/${instId}`),
  updateInstallment: (planId, instId, data) => api.patch(`${AR}/payment-plans/${planId}/installments/${instId}`, data),
  waiveInstallment:  (planId, instId, data) => api.post(`${AR}/payment-plans/${planId}/installments/${instId}/waive`, data),
  raiseInvoice:      (planId, instId, data) => api.post(`${AR}/payment-plans/${planId}/installments/${instId}/raise-invoice`, data),
  linkReceipt:       (planId, instId, data) => api.post(`${AR}/payment-plans/${planId}/installments/${instId}/link-receipt`, data),
  linkAdvance:       (planId, instId, data) => api.post(`${AR}/payment-plans/${planId}/installments/${instId}/link-advance`, data),

  // Reminders (MOCK UAT only)
  sendReminder:      (planId, data)    => api.post(`${AR}/payment-plans/${planId}/send-reminder`, data),
  getReminders:      (planId)          => api.get(`${AR}/payment-plans/${planId}/reminders`),
};

// ── PAY-01D: Gateway Payments API ─────────────────────────────────────────────
export const gatewayApi = {
  // Orders
  getSummary:         ()                    => api.get(`${AR}/gateway-orders/summary`),
  listOrders:         (params)              => api.get(`${AR}/gateway-orders`, { params }),
  getOrder:           (orderId)             => api.get(`${AR}/gateway-orders/${orderId}`),
  expireOrder:        (orderId)             => api.post(`${AR}/gateway-orders/${orderId}/expire`),
  initiateRefund:     (orderId, data)       => api.post(`${AR}/gateway-orders/${orderId}/refund`, data),

  // Create
  createQR:           (data)                => api.post(`${AR}/gateway-orders/qr`, data),
  createPaymentLink:  (data)                => api.post(`${AR}/gateway-orders/payment-link`, data),

  // Config
  getConfig:          ()                    => api.get(`${AR}/gateway-config`),

  // Settlements
  listSettlements:    (params)              => api.get(`${AR}/gateway-settlements`, { params }),
  importSettlement:   (data)                => api.post(`${AR}/gateway-settlements/import`, data),
};


// ── ARGOV-INT-01: Integration Control Center API ──────────────────────────────
const INT = "/integrations";
export const integrationApi = {
  // Provider catalog (public, no auth)
  getCatalog:            ()                  => api.get(`${INT}/catalog`),

  // Integration CRUD
  list:                  (params)            => api.get(`${INT}`, { params }),
  create:                (data)              => api.post(`${INT}`, data),
  get:                   (id)                => api.get(`${INT}/${id}`),
  update:                (id, data)          => api.patch(`${INT}/${id}`, data),
  delete:                (id)                => api.delete(`${INT}/${id}`),

  // Lifecycle
  test:                  (id, data)          => api.post(`${INT}/${id}/test`, data),
  requestActivation:     (id, data)          => api.post(`${INT}/${id}/request-activation`, data),
  approveActivation:     (id, data)          => api.post(`${INT}/${id}/approve-activation`, data),
  suspend:               (id)                => api.post(`${INT}/${id}/suspend`),
  revoke:                (id)                => api.post(`${INT}/${id}/revoke`),
  rotateSecret:          (id, data)          => api.post(`${INT}/${id}/rotate-secret`, data),

  // Monitoring
  getHealth:             (id)                => api.get(`${INT}/${id}/health`),
  getAudit:              (id, params)        => api.get(`${INT}/${id}/audit`, { params }),
  getEvents:             (params)            => api.get(`${INT}/events/log`, { params }),
};

// ── P06 MCR-P2-001: AR Dunning API ───────────────────────────────────────────
export const dunningApi = {
  prepareBatch:  (data)        => api.post("/ar/dunning/prepare", data),
  getLog:        (params)      => api.get("/ar/dunning/log", { params }),
};

// ── CRM-UX-03: Responsibility Resolution API ─────────────────────────────────
export const responsibilityApi = {
  getResponsibility: (recordType, recordId) =>
    api.get(`/crm/responsibility/${recordType}/${recordId}`),
  getMyActions: (limit = 8) =>
    api.get("/crm/my-actions", { params: { limit } }),
};

// ── CRM-UX-01 Stage 03: Personalized List Columns ────────────────────────────
export const userPrefsApi = {
  getListView:     (viewId)          => api.get(`/crm/user-preferences/list-view/${viewId}`),
  saveListView:    (viewId, data)    => api.put(`/crm/user-preferences/list-view/${viewId}`, data),
  deleteListView:  (viewId)          => api.delete(`/crm/user-preferences/list-view/${viewId}`),
  getColumns:      (viewId)          => api.get(`/crm/user-preferences/list-view/${viewId}/columns`),
};

// ── CRM-UX-01 Stage 04: Admin-Configurable Labels ────────────────────────────
export const labelApi = {
  list:       (params)               => api.get("/admin/label-config", { params }),
  upsert:     (labelKey, data)       => api.put(`/admin/label-config/${labelKey}`, data),
  remove:     (labelKey)             => api.delete(`/admin/label-config/${labelKey}`),
  getAudit:   (params)               => api.get("/admin/label-config/audit", { params }),
};

